/**
 * Stage 3: Contact Information / SMS Consent
 *
 * TCPA-compliant SMS consent collection
 * Phone number must be collected if consent is given
 *
 * Location: src/components/booking/flow/stages/Stage3Contact.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 *
 * TCPA Requirements:
 * - Checkbox must default to unchecked
 * - Clear consent language
 * - Phone number required when consent given
 * - Opt-out mechanism clearly stated
 */

import { useState } from 'react';

/**
 * Stage3Contact Component
 *
 * @param {Object} props
 * @param {Object} props.bookingData - Current booking form data
 * @param {Function} props.onNext - Handler for proceeding to next stage
 * @param {Function} props.onBack - Handler for going back
 * @param {Function} props.trackProgressAction - Handler for tracking progress
 * @returns {JSX.Element}
 */
export default function Stage3Contact({ bookingData, onNext, onBack, trackProgressAction }) {
  const [formData, setFormData] = useState({
    smsConsent: bookingData.smsConsent || false,
    phoneNumber: bookingData.phoneNumber || '',
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
    trackProgressAction(3, 'field_completed');
  };

  const validateForm = () => {
    const newErrors = {};

    // Phone number required if SMS consent given
    if (formData.smsConsent && !formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required for SMS updates';
    }

    // Basic phone number format validation
    if (formData.smsConsent && formData.phoneNumber.trim()) {
      const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
      if (!phoneRegex.test(formData.phoneNumber.replace(/\s/g, ''))) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (  _e) => {
    _e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onNext({
      smsConsent: formData.smsConsent,
      phoneNumber: formData.phoneNumber,
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Stay Connected</h2>
          <p className="text-gray-600 mt-1">Your guide may need to reach you about trip details</p>
        </div>

        {/* Benefits Section */}
        <div className="bg-emerald-50 rounded-lg p-4 space-y-2">
          <p className="font-medium text-emerald-900">Benefits of SMS updates:</p>
          <ul className="text-sm text-emerald-800 space-y-1">
            <li>✓ Weather alerts and trip updates</li>
            <li>✓ Meeting location coordination</li>
            <li>✓ Direct communication with your guide</li>
          </ul>
        </div>

        {/* TCPA Compliance & Consent */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          {/* Legal Disclosure */}
          <p className="text-xs text-gray-600">
            By checking below, you consent to receive automated texts about your trip. Message frequency varies. Msg & data rates may apply. Reply STOP to unsubscribe.{' '}
            <a href="/terms" className="text-emerald-600 underline">Terms</a> |{' '}
            <a href="/privacy" className="text-emerald-600 underline">Privacy</a>
          </p>

          {/* Consent Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.smsConsent}
              onChange={(e) => handleInputChange('smsConsent', e.target.checked)}
              className="w-5 h-5 mt-0.5 text-emerald-500 rounded border-gray-300 focus:ring-emerald-500"
            />
            <span className="text-sm font-medium">Yes, I want trip updates via text</span>
          </label>

          {/* Phone Number Input (shown when consent given) */}
          {formData.smsConsent && (
            <div className="pt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                  errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="(555) 123-4567"
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
              )}
            </div>
          )}
        </div>

        {/* Skip Message */}
        {!formData.smsConsent && (
          <p className="text-xs text-gray-500 text-center">
            You can skip SMS, but your guide may have limited ways to reach you.
          </p>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
